import {
  SB,
  payloadComputedProperty,
  searchableComputedProperty,
} from '@play-co/replicant';
import { State } from '../../State';
import { calcBracket } from './match2-tournament.utils';
import { match2TournamentRuleset } from './match2-tournament.ruleset';

const ruleset = match2TournamentRuleset;

export const match2Properties = {
  /*
   *   Match2 tournament is enabled
   *   Based on AB tests for now, but mahy have other conditions in the future
   */
  match2TournamentsEnabled: searchableComputedProperty(
    SB.boolean(),
    (state: State) => {
      // Feature unlock level
      if (state.match2.level < ruleset.unlockLevel) {
        return false;
      }

      // Otherwise, return true
      return true;
    },
  ),

  match2FlyingHighTournamentTestEnabled: searchableComputedProperty(
    SB.boolean(),
    (state: State) => {
      // If the new tournament will not be enabled, return false

      // Note: We intentionally use the non-state-checking version of the function here
      // - this will calculate the property and add the player to the pool as expected,
      // even if the player hasn't logged in and the actual test hasn't assigned in state yet
      return state.match2.level >= ruleset.flyingHighUnlockLevel;
    },
  ),

  match2Tournaments: payloadComputedProperty(
    SB.array(
      SB.object({
        eventId: SB.string(),
        score: SB.int(),
        scoreUpdatedAt: SB.int(),
      }),
    ),
    (state: State) => {
      return state.match2?.tournament?.events
        ? Object.entries(state.match2.tournament.events).map(
            ([eventId, data]) => ({
              eventId,
              score: data.player.score,
              scoreUpdatedAt: data.player.scoreUpdatedAt,
            }),
          )
        : [];
    },
  ),

  match2Level5Bracket: searchableComputedProperty(SB.int(), (state: State) =>
    calcBracket(5, state.match2?.level),
  ),

  match2Booster5Bracket: searchableComputedProperty(
    SB.int(),
    (state: State) => {
      return calcBracket(
        5,
        state.match2?.boosters.dart,
        state.match2?.boosters.bullet,
        state.match2?.boosters.drill,
        state.match2?.boosters.roulette,
      );
    },
  ),

  match2Powerup5Bracket: searchableComputedProperty(
    SB.int(),
    (state: State) => {
      return calcBracket(
        5,
        state.match2?.powerups.rocket,
        state.match2?.powerups.bomb,
        state.match2?.powerups.cube,
      );
    },
  ),

  gems50Bracket: searchableComputedProperty(SB.int(), (state: State) =>
    calcBracket(50, state.inventory.premium.free, state.inventory.premium.paid),
  ),

  match2TournamentLastStartedAt: searchableComputedProperty(
    SB.int(),
    (state: State) => {
      return state.match2?.tournament?.lastStartedAt ?? 0;
    },
  ),

  match2CurrentTournamentEndAt: searchableComputedProperty(
    SB.int(),
    (state: State) => {
      return state.match2?.tournament?.currentEndAt ?? 0;
    },
  ),

  match2CurrentTournamentType: searchableComputedProperty(
    SB.string(),
    (state: State) => {
      return state.match2?.tournament?.currentType ?? 'star';
    },
  ),

  match2TournamentLastWinAt: searchableComputedProperty(
    SB.int(),
    (state: State) => {
      return state.match2?.tournament?.lastWinAt ?? 0;
    },
  ),

  match2TournamentLastRank: searchableComputedProperty(
    SB.int(),
    (state: State) => {
      return state.match2?.tournament?.lastRank ?? 0;
    },
  ),

  match2TournamentLastMatchMakingCriteria: searchableComputedProperty(
    SB.string(),
    (state: State) => {
      return state.match2?.tournament?.lastMatchMakingCriteria ?? '';
    },
  ),

  match2Level: searchableComputedProperty(SB.int(), (state: State) => {
    return state.match2?.level ?? 0;
  }),
};
